import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Alert, Snackbar } from '@mui/material';
import { logout } from '../slice/authSlice';
import { activateMarket, setActiveMarketLocal } from '../slice/marketSlice';
import { disconnectSocket } from '../socket/socket.js';
import WebNotificationSetting from './WebNotificationSetting';

const SettingsList = ({ isVisible, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [marketError, setMarketError] = useState('');
  const [marketSuccess, setMarketSuccess] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const activeMarket = useSelector(
    (state) => state.market?.activeMarket || state.auth?.user?.activeMarket || null
  );
  const marketStatus = useSelector((state) => state.market?.status || 'idle');

  const handleLogout = () => {
    dispatch(logout())
      .unwrap()
      .then(() => {
        setTimeout(() => navigate('/signin'), 100);
        disconnectSocket();
        onClose(); // Close settings dropdown
      })
      .catch((err) => console.error("Logout error:", err));
  };

  const handleMarketChange = async (nextMarket) => {
    if (!nextMarket || nextMarket === activeMarket) return;

    const previousMarket = activeMarket ?? null;
    setMarketError('');
    setMarketSuccess('');
    dispatch(setActiveMarketLocal(nextMarket));

    try {
      await dispatch(activateMarket(nextMarket)).unwrap();
      const marketLabel = nextMarket === 'domestic' ? 'Domestic' : 'Export';
      setMarketSuccess(`Market changed to ${marketLabel}.`);
      setToast({
        open: true,
        message: `Market changed to ${marketLabel}.`,
        severity: 'success'
      });
      setTimeout(() => setMarketSuccess(''), 2500);
    } catch (error) {
      dispatch(setActiveMarketLocal(previousMarket));
      const message = typeof error === 'string' && error ? error : 'Unable to switch market';
      setMarketError(message);
      setToast({
        open: true,
        message,
        severity: 'error'
      });
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
      <div style={{
        position: 'absolute',
        top: '35px',
        right: '0',
        background: 'white',
        border: '1px solid #ddd',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '280px',
        zIndex: 1000,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
      
      {/* Header */}
      <div style={{ 
        padding: '12px 16px', 
        borderBottom: '1px solid #eee',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#f8f9fa',
        borderRadius: '8px 8px 0 0'
      }}>
        <strong style={{ color: '#333', fontSize: '16px' }}>⚙️ Settings</strong>
        <button 
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: '#666',
            padding: '0',
            width: '24px',
            height: '24px'
          }}
        >
          ✕
        </button>
      </div>

      {/* Settings List */}
      <div>
        {/* Web Notification Setting */}
        <WebNotificationSetting />

        {/* Market Selection */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #eee'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '8px' }}>
            Market
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              disabled={marketStatus === 'loading'}
              onClick={() => handleMarketChange('domestic')}
              style={{
                flex: 1,
                padding: '8px 10px',
                borderRadius: '6px',
                border: activeMarket === 'domestic' ? '1px solid #1976d2' : '1px solid #ddd',
                background: activeMarket === 'domestic' ? '#eaf3ff' : '#fff',
                color: '#333',
                cursor: marketStatus === 'loading' ? 'not-allowed' : 'pointer',
                opacity: marketStatus === 'loading' ? 0.7 : 1,
                fontSize: '13px',
                fontWeight: 500
              }}
            >
              Domestic
            </button>
            <button
              type="button"
              disabled={marketStatus === 'loading'}
              onClick={() => handleMarketChange('export')}
              style={{
                flex: 1,
                padding: '8px 10px',
                borderRadius: '6px',
                border: activeMarket === 'export' ? '1px solid #1976d2' : '1px solid #ddd',
                background: activeMarket === 'export' ? '#eaf3ff' : '#fff',
                color: '#333',
                cursor: marketStatus === 'loading' ? 'not-allowed' : 'pointer',
                opacity: marketStatus === 'loading' ? 0.7 : 1,
                fontSize: '13px',
                fontWeight: 500
              }}
            >
              Export
            </button>
          </div>
          {marketError && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#d32f2f' }}>
              {marketError}
            </div>
          )}
          {!marketError && marketSuccess && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#2e7d32' }}>
              {marketSuccess}
            </div>
          )}
        </div>
         
        {/* Document Management */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #eee',
          cursor: 'pointer'
        }}
        onClick={() => { navigate('/documents'); onClose(); }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '18px' }}>📁</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                Document Management
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Manage files and folders
              </div>
            </div>
          </div>
        </div>

        {/* Future Settings - Coming Soon */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #eee',
          opacity: 0.5,
          cursor: 'not-allowed'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '18px' }}>🎨</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                Theme Settings
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Coming soon...
              </div>
            </div>
          </div>
        </div>

        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #eee',
          opacity: 0.5,
          cursor: 'not-allowed'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '18px' }}>🔐</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                Privacy Settings
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Coming soon...
              </div>
            </div>
          </div>
        </div>

        <div style={{
          padding: '12px 16px',
          opacity: 0.5,
          cursor: 'not-allowed'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '18px' }}>📧</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                Email Preferences
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Coming soon...
              </div>
            </div>
          </div>
        </div>

        {/* SignOut */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #eee',
          cursor: 'pointer',
          backgroundColor: '#fff'
        }}
        onClick={handleLogout}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '18px', color: '#f44336' }}>🚪</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#f44336' }}>
                Sign Out
              </div>
              <div style={{ fontSize: '12px', color: '#999' }}>
                Logout from your account
              </div>
            </div>
          </div>
        </div>
      </div>

      </div>
    </>
  );
};

export default SettingsList;
